/**
 * Provider-aware payment input wrapper.
 *
 * Picks the card form for the restaurant's routed processor
 * (`restaurant.providerRouting.defaultProvider`). TRX is the only processor
 * today: its JS-library-backed `TrxJsLibraryInput` mints its own PayPage
 * session through the Vite-only client barrel and renders Apple/Google Pay.
 * `@order-up/common` owns no fetch client, and the shared `<TrxCardFields>`
 * already covers the session-as-a-prop case, which is why this branch stays
 * local.
 *
 * The encrypted payload is extracted IMPERATIVELY at submit time via
 * `extract()` (on the ref) — the library's extract call deactivates the
 * fields, so it is submit-time only. `extract()` returns a union
 * discriminated on `provider`, so a second processor is one more arm here.
 *
 * FAIL CLOSED: a provider this build has no card form for (a stored legacy
 * routing value) renders a misconfiguration notice and `extract()` throws —
 * an unknown provider is never treated as TRX.
 */

import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react';
import { ErrorNotice, type TranslatedError } from '@order-up/common';
export type { TrxPayPageSessionMinter, TrxWalletPaymentResult } from './TrxJsLibraryInput';
import TrxJsLibraryInput, {
    type TrxPayPageSessionMinter,
    type TrxWalletPaymentResult,
    type TrxEncryptedPayload,
    type TrxJsLibraryInputHandle,
} from './TrxJsLibraryInput';

// ─────────────────────────── public types ──────────────────────────────

/** The processors this app has a card form for (`PAYMENT_PROVIDERS`). */
export type PaymentProvider = 'trx';

/** Narrow an untrusted routing value to a provider with a card form here. */
export function isPaymentProvider(value: unknown): value is PaymentProvider {
    return value === 'trx';
}

/**
 * Discriminated union returned from the wrapper's imperative
 * `extract()` method. The `provider` discriminator tells the parent
 * which downstream submission path to take.
 */
export type ExtractedPayment = {
    readonly provider: 'trx';
    readonly encrypted: TrxEncryptedPayload;
};

export interface PaymentInputHandle {
    /**
     * Extract the payment payload for submission — triggers the library's
     * submit-time extract.
     *
     * Throws on: library not initialized, library-side validation failure,
     * an empty Encrypted block, or a provider with no card form here.
     */
    extract(): Promise<ExtractedPayment>;
    /** Re-activate fields after a failed extract for retry. */
    enableFields(): void;
    /**
     * Currently-active provider. Useful for parents that need to
     * branch UI (e.g. show/hide "save card" toggle which has different
     * semantics across providers).
     */
    readonly provider: PaymentProvider;
}

export interface PaymentInputProps {
    /** Active provider. Caller resolves from `restaurant.providerRouting`. */
    readonly provider: PaymentProvider;
    /** Required for the TRX session-mint call. */
    readonly tenantId: number;
    /** Required for the TRX session-mint call. */
    readonly restaurantId: number;
    /** Card vs. ACH. */
    readonly paymentType?: 'card' | 'ach';
    /** Required for TRX innerAction = Sale | Auth (server enforces). */
    readonly amount?: {
        readonly minorUnits: number;
        readonly currency: 'USD' | 'CAD';
    };
    /** Optional — TRX-only, echoes onto Reference.PurchaseId. */
    readonly purchaseId?: string;
    /** Optional — TRX-only, echoes onto Reference.Guid for webhook dedup. */
    readonly idempotencyKey?: string;
    /**
     * TRX-only — where the PayPage session comes from (see
     * {@link TrxPayPageSessionMinter}). Passed ONLY by the POS keyed-entry page,
     * whose native host mints it.
     */
    readonly mintSession?: TrxPayPageSessionMinter;
    /**
     * Surface validation/init errors to the parent, already
     * customer-friendly.
     */
    readonly onValidationError?: (message: string) => void;
    /**
     * Fires when the JS library's field iframes flip between loading and
     * ready. Parents gate their submit button on this; the wrapper ref
     * mounting alone does NOT mean extract() will succeed.
     */
    readonly onReady?: (ready: boolean) => void;
    /** Render Apple Pay / Google Pay buttons in the card form. */
    readonly enableWallets?: boolean;
    /**
     * Fires when the customer completes an Apple/Google Pay sheet.
     * The parent submits the checkout immediately with this payload.
     */
    readonly onWalletResult?: (result: TrxWalletPaymentResult) => void;
}

// ─────────────────────────── implementation ────────────────────────────

/**
 * Misconfiguration notice for a routed provider this build has no card form
 * for. Not a guest mistake and not retryable here. Fails CLOSED: no fields
 * render, so nothing can be submitted.
 */
const UNSUPPORTED_PROVIDER: TranslatedError = {
    code: 'PAYMENTS_PROVIDER_UNSUPPORTED',
    title: 'Card payments are unavailable',
    message: "This restaurant can't take a card online right now.",
    severity: 'service',
    retryable: false,
    technicalDetail: 'PaymentInput: no card form for the routed provider',
    nextSteps: ['Please pay another way, or contact the restaurant.'],
};

const PaymentInput = forwardRef<PaymentInputHandle, PaymentInputProps>(
    function PaymentInput(
        {
            provider,
            tenantId,
            restaurantId: locationNumericId,
            paymentType = 'card',
            amount,
            purchaseId,
            idempotencyKey,
            mintSession,
            onValidationError,
            onReady,
            enableWallets,
            onWalletResult,
        },
        handleRef,
    ) {
        const trxRef = useRef<TrxJsLibraryInputHandle>(null);
        const supported = isPaymentProvider(provider);

        const extract = useCallback(async (): Promise<ExtractedPayment> => {
            if (!supported) {
                throw new Error('Card payments are unavailable for this restaurant.');
            }
            if (!trxRef.current) {
                throw new Error(
                    'Payment form is still loading — please wait a moment and try again.',
                );
            }
            const encrypted = await trxRef.current.extractEncryptedData();
            return { provider: 'trx', encrypted };
        }, [supported]);

        const enableFields = useCallback(() => {
            trxRef.current?.enableFields();
        }, []);

        useImperativeHandle(
            handleRef,
            () => ({ extract, enableFields, provider }),
            [extract, enableFields, provider],
        );

        if (!supported) {
            return <ErrorNotice translated={UNSUPPORTED_PROVIDER} />;
        }

        return (
            <TrxJsLibraryInput
                ref={trxRef}
                tenantId={tenantId}
                restaurantId={locationNumericId}
                paymentType={paymentType}
                {...(amount !== undefined ? { amount } : {})}
                {...(purchaseId !== undefined ? { purchaseId } : {})}
                {...(idempotencyKey !== undefined ? { idempotencyKey } : {})}
                {...(mintSession !== undefined ? { mintSession } : {})}
                {...(onValidationError !== undefined ? { onValidationError } : {})}
                {...(onReady !== undefined ? { onReady } : {})}
                {...(enableWallets !== undefined ? { enableWallets } : {})}
                {...(onWalletResult !== undefined ? { onWalletResult } : {})}
            />
        );
    },
);

export default PaymentInput;
